import React, { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiHeart, FiShoppingBag } from 'react-icons/fi';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import CountdownTimer from './CountdownTimer';
import API from '../api';
import { getImageUrl, handleImgError } from '../utils/imageUtils';

// Stock urgency badge component
const StockBadge = ({ stock }) => {
  if (stock === 0) {
    return (
      <span className="absolute top-3 right-3 z-10 bg-gray-600/90 text-white text-[10px] px-2.5 py-1
                       rounded-full font-semibold uppercase tracking-wide backdrop-blur-sm">
        Out of Stock
      </span>
    );
  }
  if (stock >= 1 && stock <= 4) {
    return (
      <span className="absolute bottom-3 left-3 z-10 bg-red-500 text-white text-[10px] px-2.5 py-1
                       rounded-full font-semibold animate-pulse shadow-lg">
        🔥 Only {stock} left — order soon!
      </span>
    );
  }
  if (stock >= 5 && stock <= 10) {
    return (
      <span className="absolute bottom-3 left-3 z-10 bg-amber-500 text-white text-[10px] px-2.5 py-1
                       rounded-full font-semibold shadow-md">
        ⚡ Only {stock} left
      </span>
    );
  }
  return null; // stock > 10
};

const ProductCard = React.memo(({ product }) => {
  const { addToCart } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const [isHovered, setIsHovered] = useState(false);
  const [recentBuyers, setRecentBuyers] = useState(null);
  const hasFetchedBuyers = useRef(false);

  const liked = isInWishlist(product._id);

  const discount = product.discount > 0 
    ? product.discount 
    : (product.mrp && product.mrp > product.price 
        ? Math.round(((product.mrp - product.price) / product.mrp) * 100) 
        : 0);

  const mainImage = getImageUrl(product.images?.[0], 'thumbnail');
  const hoverImage = product.images?.[1] ? getImageUrl(product.images[1], 'thumbnail') : mainImage;
  const isOutOfStock = product.stock === 0;

  // Flash sale check
  const isFlashSale = product.salePrice && product.saleEndTime && new Date(product.saleEndTime) > new Date() && (!product.saleStartTime || new Date(product.saleStartTime) <= new Date());
  const saleSavingsPercent = isFlashSale ? Math.round(((product.price - product.salePrice) / product.price) * 100) : 0;

  // Lazy-fetch recent buyers only on first hover (avoid N+1 API storm on page load)
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    if (!hasFetchedBuyers.current && product.sold > 0) {
      hasFetchedBuyers.current = true;
      API.get(`/api/products/${product._id}/recent-buyers`)
        .then(({ data }) => {
          if (data.recentBuyers > 0) setRecentBuyers(data.recentBuyers);
        })
        .catch(() => {}); // silently fail — social proof is non-critical
    }
  }, [product._id, product.sold]);

  return (
    <div
      className={`group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl
                 transition-all duration-500 relative card-hover ${isOutOfStock ? 'opacity-80' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image container */}
      <div className="relative aspect-square overflow-hidden bg-white">
        <Link to={`/products/${product._id}`}>
          <img
            src={isHovered ? hoverImage : mainImage}
            // eslint-disable-next-line jsx-a11y/img-redundant-alt
            alt={`${product.name} - Poshe Styles Premium Accessories`}
            className={`w-full h-full object-contain transition-transform duration-700
                       group-hover:scale-105 ${isOutOfStock ? 'grayscale-[30%]' : ''}`}
            onError={handleImgError}
            loading="lazy"
          />
          {/* Out of stock overlay */}
          {isOutOfStock && (
            <div className="absolute inset-0 bg-white/30 flex items-center justify-center">
              <span className="bg-gray-800/80 text-white text-sm px-4 py-2 rounded-lg font-semibold
                             backdrop-blur-sm">
                Out of Stock
              </span>
            </div>
          )}
        </Link>

        {/* Category badge - top left */}
        <span className="absolute top-3 left-3 bg-navy/80 text-white text-[10px] px-2.5 py-1
                         rounded-full font-medium uppercase tracking-wider backdrop-blur-sm z-10">
          {product.category}
        </span>

        {/* Discount badge - top left below category */}
        {discount > 0 && (
          <span className="absolute top-10 left-3 bg-gold text-white text-[10px] px-2.5 py-1
                           rounded-full font-bold z-10">
            -{discount}%
          </span>
        )}

        {/* Stock urgency badge */}
        {!isOutOfStock && <StockBadge stock={product.stock} />}

        {/* Wishlist heart - top right */}
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleWishlist(product._id, product.name); }}
          aria-label="Toggle wishlist"
          className={`absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center
                      transition-all duration-300 shadow-md z-10 ${
                        liked
                          ? 'bg-red-500 text-white'
                          : 'bg-white/90 text-charcoal hover:bg-red-50 hover:text-red-500'
                      }`}
        >
          <FiHeart size={16} fill={liked ? 'currentColor' : 'none'} />
        </button>

        {/* Out of stock badge on top-right if out of stock */}
        {isOutOfStock && <StockBadge stock={0} />}

        {/* Quick add to cart - slides up on hover */}
        <div className={`absolute bottom-0 left-0 right-0 p-3 transition-all duration-300 transform z-20 ${
          isHovered ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}>
            <button
              onClick={(e) => {
                e.preventDefault();
                if (!isOutOfStock) addToCart(product, 1, product.colors?.[0] || '');
              }}
              disabled={isOutOfStock}
              className={`w-full py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-colors shadow-lg ${
                isOutOfStock ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-gold hover:bg-gold-dark text-white'
              }`}
            >
              <FiShoppingBag size={16} /> {isOutOfStock ? 'Out of Stock' : 'Quick Add'}
            </button>
          </div>
      </div>

      {/* Product info */}
      <Link to={`/products/${product._id}`} className="block p-4">
        <h3 className="font-semibold text-charcoal text-sm mb-1 line-clamp-1
                       group-hover:text-gold transition-colors">
          {product.name}
        </h3>

        {/* Social proof: Sold count + recent buyers */}
        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
          {product.sold > 0 && (
            <span className="text-[10px] text-gray-400 flex items-center gap-1">
              📦 Sold {product.sold.toLocaleString('en-IN')} times
            </span>
          )}
          {recentBuyers > 0 && (
            <span className="text-[10px] text-orange-500 font-medium flex items-center gap-1 animate-pulse">
              🔥 {recentBuyers} bought in last 24h
            </span>
          )}
        </div>

        {/* Flash sale countdown */}
        {isFlashSale && (
          <div className="mb-1.5">
            <CountdownTimer endTime={product.saleEndTime} compact className="text-red-600 bg-red-50 px-2 py-0.5 rounded-full" />
          </div>
        )}

        {/* Price */}
        <div className="flex items-center gap-2 mb-1.5">
          <span className={`font-bold text-lg ${isFlashSale ? 'text-red-600' : 'text-gold'}`}>
            ₹{(isFlashSale ? product.salePrice : (discount > 0 ? (product.mrp - (product.mrp * discount / 100)) : product.price))?.toLocaleString('en-IN')}
          </span>
          {(isFlashSale || product.mrp > product.price || discount > 0) && (
            <span className="text-gray-400 text-sm line-through">
              ₹{(isFlashSale ? product.price : product.mrp)?.toLocaleString('en-IN')}
            </span>
          )}
          {isFlashSale && saleSavingsPercent > 0 && (
            <span className="text-[9px] bg-red-600 text-white px-1.5 py-0.5 rounded-full font-bold">
              -{saleSavingsPercent}%
            </span>
          )}
        </div>

        {/* Color dots */}
        {product.colors && product.colors.length > 0 && (
          <div className="flex gap-1.5 mb-1.5">
            {product.colors.slice(0, 4).map((color, i) => (
              <span
                key={i}
                title={color}
                className="w-4 h-4 rounded-full border-2 border-gray-200"
                style={{
                  backgroundColor:
                    color.toLowerCase() === 'multi-color'
                      ? 'linear-gradient(135deg, #ff0000, #00ff00, #0000ff)'
                      : color.toLowerCase(),
                  background:
                    color.toLowerCase() === 'multi-color'
                      ? 'linear-gradient(135deg, #ff6b6b, #feca57, #48dbfb, #ff9ff3)'
                      : color.toLowerCase() === 'natural'
                      ? '#d4a574'
                      : color.toLowerCase() === 'cognac'
                      ? '#9a4e1c'
                      : color.toLowerCase() === 'taupe'
                      ? '#8b7d6b'
                      : color.toLowerCase() === 'rose gold'
                      ? '#b76e79'
                      : color.toLowerCase() === 'dusty pink'
                      ? '#d4a5a5'
                      : color.toLowerCase() === 'blush pink'
                      ? '#de98ab'
                      : color.toLowerCase() === 'army green'
                      ? '#4b5320'
                      : color.toLowerCase() === 'forest green'
                      ? '#228b22'
                      : color.toLowerCase(),
                }}
              />
            ))}
            {product.colors.length > 4 && (
              <span className="text-xs text-gray-400 ml-1">+{product.colors.length - 4}</span>
            )}
          </div>
        )}

        {/* Rating with star and review count */}
        <div className="flex items-center gap-1.5 mt-1">
          {product.rating > 0 ? (
            <>
              <div className="flex items-center gap-0.5 bg-green-600 text-white px-1.5 py-0.5
                            rounded text-[10px] font-bold">
                <span>{product.rating?.toFixed(1)}</span>
                <span>★</span>
              </div>
              <span className="text-[10px] text-gray-400">
                ({product.numReviews} {product.numReviews === 1 ? 'review' : 'reviews'})
              </span>
            </>
          ) : (
            <span className="text-[10px] text-gray-300">No reviews yet</span>
          )}
        </div>
      </Link>
    </div>
  );
});

export default ProductCard;
